import { Dispatch, SetStateAction, useEffect } from "react";
import { motion } from "framer-motion";
import Lottie from "lottie-react";
import confettiAnimation from "../../../assets/lotties/confetti.json";
import { ReactComponent as ChevronLeftIcon } from "../../../assets/icons/chevron-left.svg";
import { ReactComponent as CloseIcon } from "../../../assets/icons/x.svg";
import { OnboardingCardView } from "./OnboardingCardView";
import { OnboardingCard } from "./types";

function ProgressBar({ total, current }: { total: number; current: number }) {
    return (
        <div className="flex items-center gap-2">
            {Array.from({ length: total }).map((_, index) => (
                <span
                    key={index}
                    className="h-[3px] w-8 rounded-full transition-colors"
                    style={{ backgroundColor: index <= current ? "var(--primary)" : "#E3DAD1" }}
                />
            ))}
        </div>
    );
}

/**
 * Multi-step Badge Center onboarding carousel. Rendered on top of the Badge
 * Center screen for first-time users. `onDismiss` is invoked for every exit —
 * the X button on any slide and the CTA on the final slide — so the server-side
 * "acknowledged" flag is set in both cases.
 */
export function BadgeCenterOnboarding({
    cards,
    onDismiss,
    step,
    setStep,
}: {
    cards: OnboardingCard[];
    onDismiss: () => void;
    step: number;
    setStep: Dispatch<SetStateAction<number>>;
}) {
    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "";
        };
    }, []);

    if (!cards.length) return null;

    const isFirst = step === 0;
    const isLast = step === cards.length - 1;
    const card = cards[step];

    const handleBack = () => {
        if (!isFirst) setStep((prev) => prev - 1);
    };

    const handleCta = () => {
        if (isLast) {
            onDismiss();
        } else {
            setStep((prev) => prev + 1);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
            <div
                className="absolute inset-0"
                style={{ backgroundColor: "rgba(0, 0, 0, 0.35)", backdropFilter: "blur(4px)" }}
            />
            <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                role="dialog"
                aria-modal="true"
                aria-label="Badge Center onboarding"
                className="relative z-10 w-[750px] h-[458px] max-w-full overflow-hidden rounded-[16px] bg-[#FBEEE2] p-8 shadow-[0_10px_25px_-5px_rgba(0,0,0,0.25)] flex flex-col"
            >
                {isLast && (
                    <Lottie
                        animationData={confettiAnimation}
                        loop={false}
                        autoplay
                        rendererSettings={{ preserveAspectRatio: "xMidYMid slice" }}
                        className="pointer-events-none absolute inset-0 w-full h-full"
                    />
                )}
                <div className="flex items-center justify-between">
                    <button
                        type="button"
                        onClick={handleBack}
                        aria-label="Go back"
                        className={`transition-opacity ${isFirst ? "invisible" : "hover:opacity-70"}`}
                    >
                        <ChevronLeftIcon className="h-6 w-6" />
                    </button>

                    <ProgressBar total={cards.length} current={step} />

                    <button
                        type="button"
                        onClick={onDismiss}
                        aria-label="Close onboarding"
                        className="hover:opacity-70 transition-opacity"
                    >
                        <CloseIcon className="h-6 w-6" />
                    </button>
                </div>

                <div className="mt-6 flex-1 min-h-0">
                    <OnboardingCardView key={step} card={card} onCtaClick={handleCta} />
                </div>
            </motion.div>
        </div>
    );
}
